import { Routes, Route } from 'react-router';
import { EmptyState } from '@dayon/design-system';

import { Dashboard } from './features/dashboard/Dashboard.tsx';
import { Players } from './features/players/Players.tsx';
import { Withdrawals } from './features/funds/Withdrawals.tsx';
import { PlayerProfile } from './features/profile/PlayerProfile.tsx';
import { Transactions } from './features/transactions/Transactions.tsx';
import { Payments } from './features/payments/Payments.tsx';
import { Games } from './features/games/Games.tsx';
import { Promotions } from './features/promotions/Promotions.tsx';
import { Points } from './features/points/Points.tsx';
import { Agents } from './features/agents/Agents.tsx';
import { Affiliates } from './features/affiliates/Affiliates.tsx';
import { Support } from './features/support/Support.tsx';
import { IdentityChecks } from './features/identity/IdentityChecks.tsx';
import { Risk } from './features/risk/Risk.tsx';
import { Reports } from './features/reports/Reports.tsx';
import { Brands } from './features/brands/Brands.tsx';
import { PlatformSettings } from './features/platform/PlatformSettings.tsx';
import { Maintenance } from './features/maintenance/Maintenance.tsx';
import { Announcements } from './features/announcements/Announcements.tsx';
import { SystemAdmin } from './features/sysadmin/SystemAdmin.tsx';
import { AccessControl } from './features/access/AccessControl.tsx';
import { Account } from './features/account/Account.tsx';

/**
 * The console's route table.
 *
 * Paths match the `path` on each item in `@dayon/rbac`'s NAV_GROUPS — the
 * sidebar and this table are two halves of one fact, and a nav entry pointing
 * at a route that does not exist is a dead link nothing else would catch.
 * The test in `nav.test.ts` asserts they agree.
 */
export function ConsoleRoutes() {
  return (
    <Routes>
      {/* THE LANDING. A reader without players.view — the PAGCOR Regulator — is sent
          on to Reports by the dashboard itself when the console opens here. That is
          decided in Dashboard.tsx, so this line keeps the one shape the route-table
          tests read. */}
      <Route path="/" element={<Dashboard />} />

      <Route path="/players" element={<Players />} />
      <Route path="/players/:id" element={<PlayerProfile />} />
      {/* Behind kyc.review in NAV_GROUPS, which is narrower than players.view:
          this queue opens onto photographs of identity documents. */}
      <Route path="/identity" element={<IdentityChecks />} />
      <Route path="/support" element={<Support />} />

      <Route path="/funds" element={<Withdrawals />} />
      <Route path="/transactions" element={<Transactions />} />
      <Route path="/payments" element={<Payments />} />

      <Route path="/promotions" element={<Promotions />} />
      <Route path="/points" element={<Points />} />
      <Route path="/agents" element={<Agents />} />
      <Route path="/affiliates" element={<Affiliates />} />

      <Route path="/risk" element={<Risk />} />
      <Route path="/reports" element={<Reports />} />

      <Route path="/games" element={<Games />} />
      <Route path="/brands" element={<Brands />} />
      <Route path="/settings" element={<PlatformSettings />} />
      <Route path="/maintenance" element={<Maintenance />} />
      <Route path="/announcements" element={<Announcements />} />
      <Route path="/system" element={<SystemAdmin />} />
      <Route path="/access" element={<AccessControl />} />

      {/* NOT A SIDEBAR MODULE, and `ACCOUNT_MENU_ROUTES` carries the reasoning:
          it has no permission key, every role has it, and the product IA names
          twenty modules — a count three assertions depend on. It is reached from
          the account menu at the top right, and `nav.test.ts` reads the same
          constant to excuse it from the orphan check, so a path excused there
          must genuinely be linked from that menu. */}
      <Route path="/account" element={<Account />} />

      <Route
        path="*"
        element={
          <EmptyState icon="compass" title="No such page">
            That address does not match any module in this console. Use the sidebar — it shows
            every module your role can open.
          </EmptyState>
        }
      />
    </Routes>
  );
}
